import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl, FormattedMessage } from '@kyruus/intl';
import { AppointmentInfo as BaseAppointmentInfo } from '@kyruus/appointment-info';
import PleaseCallMessage from './please-call-message';
import PhoneNumberLink from './phone-number-link';
import { StyledBanner } from './styles';

const messages = defineMessages({
  globalPatientRelTitle: {
    id: 'global.patientrel.title',
    description:
      'Patient relationship text when querying availability for all providers on page.',
    defaultMessage:
      'Are you booking with a new provider or your current provider?'
  },
  globalNewPatientButtonText: {
    id: 'global.patientrel.new.buttontext',
    description:
      'Button text for "new" patientRel button when querying availability for all providers on page.',
    defaultMessage: 'A New Provider'
  },
  globalExistingPatientButtonText: {
    id: 'global.patientrel.existing.buttontext',
    description:
      'Button text for "existing" patientRel button when querying availability for all providers on page.',
    defaultMessage: 'Your Current Provider'
  },
  patientRelTitle: {
    id: 'step_title.existing_patient',
    description: 'Title to render above patient rel buttons.',
    defaultMessage: 'Have you seen {display_name} before?'
  },
  appointmentPurposeTitle: {
    id: 'step_title.appointment_reason',
    description: 'Text for the appointment purpose step title',
    defaultMessage: 'What is the main reason for your visit?'
  },
  noapptpurposes: {
    id: 'error.no_appointment_purposes',
    description: 'Error for when the provider does not accept online booking',
    defaultMessage:
      'Sorry! {provider_name} does not accept {patient_status} patients via online booking.'
  },
  noapptpurposesGlobal: {
    id: 'error.no_appointment_purposes.global',
    description: 'Error for when all providers do not accept online booking',
    defaultMessage:
      "Sorry! We can't find any providers who accept {patient_status} patients via online booking."
  },
  existingPatientButtonText: {
    id: 'patientrel.existing.buttontext',
    description:
      'Button text for "existing" patientRel button when querying availability for a single provider.',
    defaultMessage: 'Yes'
  },
  newPatientButtonText: {
    id: 'patientrel.new.buttontext',
    description:
      'Button text for "new" patientRel button when querying availability for a single provider.',
    defaultMessage: 'No'
  },
  established: {
    id: 'patient.established',
    description: 'Established patient type',
    defaultMessage: 'established'
  },
  new: {
    id: 'patient.new',
    description: 'New patient type',
    defaultMessage: 'new'
  },
  contactoffice: {
    id: 'error.contact_office',
    description: 'Tell user to contact office directly to book appt',
    defaultMessage:
      'Please contact the office directly at {phone_number_link} to book an appointment.'
  }
});

function AppointmentInfo({
  patientRel,
  purpose,
  onPatientRelSelect,
  onSelectPurpose,
  patientRelOptions,
  providerDisplayName,
  providerPhone,
  availabilityButtonsConfig,
  isPurposeLoading,
  log,
  patientRelHasNoAvailability,
  purposeHasNoAvailability,
  hidePatientRel,
  apptPurposeFilterProps,
  formName,
  intl
}) {
  const isGlobal = !providerDisplayName;

  if (isGlobal && !isPurposeLoading) {
    const optionsForCurrentPatientRel = patientRelOptions[patientRel] || [];
    patientRelHasNoAvailability = !optionsForCurrentPatientRel.length;
  }

  const shouldShowPurpose =
    isPurposeLoading || (patientRel && !patientRelHasNoAvailability);

  const patientRelTitle = isGlobal ? (
    <FormattedMessage {...messages.globalPatientRelTitle} />
  ) : (
    <FormattedMessage
      {...messages.patientRelTitle}
      values={{ display_name: providerDisplayName }}
    />
  );

  const apptPurposeTitle = (
    <FormattedMessage {...messages.appointmentPurposeTitle} />
  );

  // customers with read-only Availability Tiles will not have a DBW config
  // instead use application string templates
  const localButtonConfig = availabilityButtonsConfig ?? [
    {
      patient_type: 'new',
      button_text: intl.formatMessage(messages.newPatientButtonText)
    },
    {
      patient_type: 'existing',
      button_text: intl.formatMessage(messages.existingPatientButtonText)
    }
  ];
  const buttonConfig = isGlobal
    ? [
        {
          patient_type: 'new',
          button_text: intl.formatMessage(messages.globalNewPatientButtonText)
        },
        {
          patient_type: 'existing',
          button_text: intl.formatMessage(
            messages.globalExistingPatientButtonText
          )
        }
      ]
    : localButtonConfig;

  const noApptPurposesMessage = isGlobal
    ? messages.noapptpurposesGlobal
    : messages.noapptpurposes;

  const NoBookingBanner = () => (
    /*
      applying lineHeight: 1.42857 here so the NoBookingBanner's spacing in PMC
      matches the NoBookingBanner in PMCDB
      TODO: https://kyruus.jira.com/browse/KENG-18878
    */
    <StyledBanner icon="!" type="warning" data-testid="NoBookingBanner">
      <FormattedMessage
        {...noApptPurposesMessage}
        values={{
          provider_name: providerDisplayName,
          patient_status:
            patientRel === 'new' ? (
              <FormattedMessage {...messages.new} />
            ) : (
              <FormattedMessage {...messages.established} />
            )
        }}
      />

      <br />

      {providerPhone && (
        <FormattedMessage
          {...messages.contactoffice}
          values={{
            phone_number_link: (
              <PhoneNumberLink phone={providerPhone} log={log} />
            )
          }}
        />
      )}
    </StyledBanner>
  );

  return (
    <React.Fragment>
      <BaseAppointmentInfo
        isApptPurposeLoading={isPurposeLoading}
        patientRelTitle={patientRelTitle}
        apptPurposeTitle={apptPurposeTitle}
        buttonConfig={buttonConfig}
        purposeOptionsByRel={patientRelOptions}
        patientRel={patientRel}
        purpose={purpose}
        onPatientRelSelect={onPatientRelSelect}
        onSelectPurpose={onSelectPurpose}
        hidePatientRel={hidePatientRel}
        showPurpose={shouldShowPurpose}
        showNoBooking={patientRelHasNoAvailability}
        NoBookingBanner={<NoBookingBanner />}
        PleaseCallMessage={
          !isGlobal &&
          !purposeHasNoAvailability && (
            <PleaseCallMessage providerPhone={providerPhone} />
          )
        }
        apptPurposeFilterProps={apptPurposeFilterProps}
        formName={formName}
      />
      {purposeHasNoAvailability ? <NoBookingBanner /> : null}
    </React.Fragment>
  );
}

AppointmentInfo.defaultProps = {
  patientRel: '',
  purpose: null,
  onPatientRelSelect: () => {},
  onSelectPurpose: () => {},
  patientRelOptions: {},
  hidePatientRel: false
};

AppointmentInfo.propTypes = {
  patientRel: PropTypes.string,
  purpose: PropTypes.object,
  onPatientRelSelect: PropTypes.func,
  onSelectPurpose: PropTypes.func,
  patientRelOptions: PropTypes.object,
  hidePatientRel: PropTypes.bool,
  apptPurposeFilterProps: PropTypes.object
};

export default injectIntl(AppointmentInfo);
